import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { fmtErr } from "@/lib/api";
import { KeyRound } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(identifier, password);
      navigate(user.role === "admin" ? "/ucp-admin" : "/dashboard");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F9FC] flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-5 pt-24 pb-16">
        <div className="bg-white border border-[#E6EBF1] rounded-2xl p-8 w-full max-w-md shadow-[0_20px_50px_rgba(10,37,64,0.08)]" data-testid="login-card">
          <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 border border-[#635BFF]/20 flex items-center justify-center mb-5">
            <KeyRound size={20} className="text-[#635BFF]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#0A2540]">{t("auth.loginTitle")}</h1>
          <p className="text-sm text-[#425466] mt-1">{t("auth.loginSubtitle")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-[#425466] text-xs font-medium">{t("auth.identifier")}</Label>
              <Input
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540] focus-visible:ring-[#635BFF]/40"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <Label className="text-[#425466] text-xs font-medium">{t("auth.password")}</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540] focus-visible:ring-[#635BFF]/40"
                autoComplete="current-password"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#635BFF] hover:bg-[#0A2540] text-white h-11 font-semibold rounded-full transition-colors"
            >
              {loading ? t("common.loading") : t("auth.loginBtn")}
            </Button>
          </form>
          <p className="text-sm text-[#425466] mt-6 text-center">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-[#635BFF] hover:underline font-semibold">
              {t("auth.createOne")}
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
